class MissionCollaborations extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-collaboration-track]');
    this.previous = this.querySelector('[data-collaboration-previous]');
    this.next = this.querySelector('[data-collaboration-next]');
    this.pause = this.querySelector('[data-collaboration-pause]');
    if (!this.track || !this.previous || !this.next) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;
    this.cards = [...this.track.children];
    const grid = this.querySelector('[data-collaboration-grid]');
    grid?.replaceChildren(...this.cards.map((card) => {
      const clone = card.cloneNode(true);
      for (const element of [clone, ...clone.querySelectorAll('*')]) {
        element.removeAttribute('id');
        element.removeAttribute('data-shopify-editor-block');
      }
      return clone;
    }));
    this.cardWidth = null;
    this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.previous.addEventListener('click', () => this.move(-1), { signal });
    this.next.addEventListener('click', () => this.move(1), { signal });
    this.pause?.addEventListener('click', () => {
      this.paused = !this.paused;
      this.pause.setAttribute('aria-pressed', String(this.paused));
    }, { signal });
    const carousel = this.querySelector('.mission-collaborations__carousel') || this.track;
    carousel.addEventListener('pointerenter', (event) => { this.hovered = event.pointerType !== 'touch'; }, { signal });
    carousel.addEventListener('pointerleave', () => { this.hovered = false; }, { signal });
    this.track.addEventListener('scroll', this.onScroll, { signal, passive: true });
    this.track.addEventListener('pointerdown', () => { this.animation = null; this.dragging = true; }, { signal });
    this.track.addEventListener('focusin', () => { this.animation = null; }, { signal });
    for (const event of ['pointerup', 'pointercancel']) {
      window.addEventListener(event, () => {
        if (this.dragging) this.manualUntil = performance.now() + 600;
        this.dragging = false;
      }, { signal });
    }
    this.track.addEventListener('wheel', () => { this.animation = null; this.manualUntil = performance.now() + 600; }, { signal, passive: true });
    this.addEventListener('shopify:block:select', () => { this.editorSelected = true; }, { signal });
    this.addEventListener('shopify:block:deselect', () => { this.editorSelected = false; }, { signal });
    this.resizeObserver = new ResizeObserver(this.rebuild);
    this.resizeObserver.observe(this.track);
    this.visibilityObserver = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      cancelAnimationFrame(this.frame);
      this.lastTime = null;
      if (this.visible) this.frame = requestAnimationFrame(this.tick);
    });
    this.visibilityObserver.observe(this);
    this.rebuild();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    this.visibilityObserver?.disconnect();
    cancelAnimationFrame(this.frame);
    this.track?.querySelectorAll('[data-collaboration-clone]').forEach((card) => card.remove());
  }

  rebuild = () => {
    const width = this.cards[0]?.getBoundingClientRect().width || 0;
    if (width === this.cardWidth && this.track.clientWidth === this.viewportWidth) return;
    this.cardWidth = width;
    this.viewportWidth = this.track.clientWidth;
    this.perPage = Math.max(1, Math.round(this.viewportWidth / width));
    this.animation = null;
    this.track.querySelectorAll('[data-collaboration-clone]').forEach((card) => card.remove());
    this.period = width * this.cards.length;
    this.looping = this.cards.length > 1 && width > 0;
    this.start = 0;
    if (this.looping) {
      const copies = Math.ceil(this.track.clientWidth / this.period) + 1;
      const cloneCards = () => this.cards.map((card) => {
        const clone = card.cloneNode(true);
        clone.setAttribute('data-collaboration-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        for (const element of [clone, ...clone.querySelectorAll('*')]) {
          element.removeAttribute('id');
          element.removeAttribute('data-shopify-editor-block');
          if (element.matches('a, button, [tabindex]')) element.setAttribute('tabindex', '-1');
        }
        return clone;
      });
      for (let index = 0; index < copies; index++) {
        this.track.prepend(...cloneCards());
        this.track.append(...cloneCards());
      }
      this.start = copies * this.period;
    }
    this.position = this.start;
    this.track.scrollLeft = this.position;
    this.previous.disabled = this.next.disabled = !this.looping;
    if (this.pause) this.pause.hidden = !this.looping;
  };

  onScroll = () => {
    if (this.animation) return;
    let position = this.track.scrollLeft;
    const focused = this.track.querySelector(':focus-visible');
    if (this.looping && !focused && (position < this.start || position >= this.start + this.period)) {
      position = this.start + ((position - this.start) % this.period + this.period) % this.period;
      this.track.scrollLeft = position;
      this.position = position;
    }
    if (Math.abs(position - this.position) > 1) this.position = position;
  };

  tick = (time) => {
    const elapsed = this.lastTime == null ? 0 : Math.min(time - this.lastTime, 50);
    this.lastTime = time;
    const focused = this.track.matches(':focus-visible') || this.track.querySelector(':focus-visible');
    if (this.animation) {
      const { from, to, started } = this.animation;
      const progress = this.motion.matches ? 1 : Math.min(1, (time - started) / 450);
      const position = from + (to - from) * (1 - (1 - progress) ** 3);
      this.position = this.start + ((position - this.start) % this.period + this.period) % this.period;
      this.track.scrollLeft = this.position;
      if (progress === 1) {
        this.animation = null;
        this.manualUntil = time + 3000;
      }
    } else if (this.looping && !document.hidden && !this.motion.matches && !this.paused && !this.dragging &&
        !focused && !this.editorSelected && !(time < this.manualUntil)) {
      const configured = Number(this.dataset.scrollSpeed ?? 40);
      const speed = Number.isFinite(configured) ? Math.max(0, Math.min(configured, 200)) : 40;
      const rate = this.hovered ? 0.5 : 1;
      this.position = this.start + ((this.position - this.start + speed * rate * elapsed / 1000) % this.period);
      this.track.scrollLeft = this.position;
    }
    this.frame = requestAnimationFrame(this.tick);
  };

  move(direction) {
    if (!this.looping) return;
    const count = this.cards.length;
    const pages = Math.ceil(count / this.perPage);
    const offset = ((this.position - this.start) / this.cardWidth % count + count) % count;
    const page = Math.floor(offset / this.perPage);
    const progress = (offset - page * this.perPage) / Math.min(this.perPage, count - page * this.perPage);
    // Left returns to the departing group; right skips the incoming group once halfway passed.
    const delta = direction > 0 ? (progress >= 0.5 ? 2 : 1) : (progress < 0.000001 ? -1 : 0);
    const target = page + delta;
    const card = Math.floor(target / pages) * count + ((target % pages + pages) % pages) * this.perPage;
    this.animation = {
      from: this.position,
      to: this.start + card * this.cardWidth,
      started: performance.now(),
    };
  }

}

if (!customElements.get('mission-collaborations')) {
  customElements.define('mission-collaborations', MissionCollaborations);
}
